// Cadastro de cliente — componente compartilhado (usado na Nova OS).
// Extraído do PDV (30/06) p/ reuso na OS.
// 30/06 (pedido do usuário): dois modos —
//   • Rápido: nome + doc/telefone/email (o mínimo, completa-se depois).
//   • Completo: + nome fantasia, IE/indicador, endereço (com busca por CEP), observação.
// Enter avança campos (enterAdvance). Cores via classes ink-*/brand-* (dark-aware).
import { useState, useEffect, useRef } from 'react'
import { UserPlus, X, Loader2, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { enterAdvance } from '@/hooks/useEnterAdvance'
import { buscarCep } from '@/lib/viacep'
import { TelefoneInput } from '@/components/TelefoneInput'
import { validarDocumento } from '@/lib/masks'

export default function ModalNovoCliente({ nomeInicial, pessoa, onClose, onCriado }: {
  nomeInicial: string
  // 19/10: quando vem preenchido, o modal EDITA essa pessoa (PUT) em vez de criar.
  // Usado pelo gate do PDV pra completar cadastro antigo sem sair da venda.
  pessoa?: any | null
  onClose: () => void
  onCriado: (p: any) => void
}) {
  // Pré-preenche: se nomeInicial só tem dígitos, vai pro documento; senão vai pro nome
  const soDigitos = nomeInicial && /^[\d.\-/\s]+$/.test(nomeInicial)
  const docInicial = soDigitos ? nomeInicial.replace(/\D/g, '') : ''
  const nomeIni = soDigitos ? '' : nomeInicial
  const pessoaTipoIni = docInicial.length === 14 ? 'PJ' : 'PF'

  // 19/10: endereço virou obrigatório pra fechar venda → abre já no Completo.
  const [modo, setModo] = useState<'rapido' | 'completo'>('completo')
  // 19/10: em modo edição, todo campo nasce do cadastro atual — senão o PUT apaga o que
  // não for digitado de novo.
  const [pessoaTipo, setPessoaTipo] = useState<'PF' | 'PJ'>(pessoa?.pessoa_tipo || pessoaTipoIni)
  const [nome, setNome] = useState(pessoa?.nome || nomeIni)
  const [documento, setDocumento] = useState(pessoa?.documento || docInicial)
  const [telefone, setTelefone] = useState(pessoa?.telefone || '')
  const [email, setEmail] = useState(pessoa?.email || '')
  // Campos do modo completo
  const [nomeFantasia, setNomeFantasia] = useState(pessoa?.nome_fantasia || '')
  const [ie, setIe] = useState(pessoa?.ie || '')
  const [indicadorIe, setIndicadorIe] = useState(pessoa?.indicador_ie || '9') // 9=não contribuinte (default PF)
  const [cep, setCep] = useState(pessoa?.cep || '')
  const [endereco, setEndereco] = useState(pessoa?.endereco || '')
  const [numero, setNumero] = useState(pessoa?.numero || '')
  const [bairro, setBairro] = useState(pessoa?.bairro || '')
  const [cidade, setCidade] = useState(pessoa?.cidade || '')
  const [uf, setUf] = useState(pessoa?.uf || '')
  const [complemento, setComplemento] = useState(pessoa?.complemento || '')
  const [codigoIbgeCidade, setCodigoIbgeCidade] = useState(pessoa?.codigo_ibge_cidade || '')
  const [observacao, setObservacao] = useState(pessoa?.observacao || '')
  const [buscandoCep, setBuscandoCep] = useState(false)
  const [salvando, setSalvando] = useState(false)
  // Duplicidade de CPF/CNPJ em tempo real (06/07 — pedido do usuário): mesma trava do
  // cadastro normal (Pessoas.tsx), agora também no cadastro rápido do PDV/OS.
  const [docDup, setDocDup] = useState<{ codigo?: string; nome: string; tipo_doc: string } | null>(null)
  const [checandoDoc, setChecandoDoc] = useState(false)
  const nomeRef = useRef<HTMLInputElement>(null)

  useEffect(() => { nomeRef.current?.focus() }, [])

  // Validação de CPF/CNPJ (01/07 — pedido do usuário): documento vazio é permitido no cadastro
  // rápido, mas se preenchido só pode salvar com os dígitos verificadores corretos.
  const docDigits = documento.replace(/\D/g, '')   // (há um `soDigitos` local booleano acima — não confundir)
  const docInvalido = docDigits.length > 0 && !validarDocumento(documento, pessoaTipo)
  const maxDoc = pessoaTipo === 'PF' ? 11 : 14
  const docExato = docDigits.length === maxDoc
  // FIX 06/07: mostra "inválido" quando ATINGE OU EXCEDE o limite (antes só no tamanho
  // exato — ao digitar um dígito a mais o erro sumia e parecia válido).
  const docMostraErro = docDigits.length >= maxDoc && docInvalido
  const docValidoOk = docExato && !docInvalido && !docDup && !checandoDoc  // feedback verde (só no tamanho certo)

  // Checagem de duplicidade em tempo real (debounce 400ms) — só com CPF(11)/CNPJ(14)
  // no tamanho exato e formato válido, pra não bater no backend à toa nem alarmar no meio.
  useEffect(() => {
    if (!docExato || docInvalido) { setDocDup(null); setChecandoDoc(false); return }
    let vivo = true
    setChecandoDoc(true)
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/cadastros/pessoas/checar-documento', {
          params: { documento: docDigits, excluir_id: pessoa?.id || undefined },
        })
        if (vivo) setDocDup(r.data?.existe ? r.data : null)
      } catch { if (vivo) setDocDup(null) }
      finally { if (vivo) setChecandoDoc(false) }
    }, 400)
    return () => { vivo = false; clearTimeout(t) }
  }, [docDigits, docExato, docInvalido, pessoa?.id])

  async function onBlurCep() {
    if (!cep.trim()) return
    setBuscandoCep(true)
    try {
      const d = await buscarCep(cep)
      if (d) {
        setEndereco(d.logradouro || endereco)
        setBairro(d.bairro || bairro)
        setCidade(d.localidade || cidade)
        setUf(d.uf || uf)
        setCodigoIbgeCidade(d.ibge || codigoIbgeCidade)
      }
    } finally { setBuscandoCep(false) }
  }

  async function salvar() {
    if (!nome.trim()) { toast.error('Informe o nome'); return }
    // PEDIDO DO CLIENTE (19/10): cliente de venda nasce com cadastro completo.
    if (nome.trim().split(/\s+/).length < 2) { toast.error('Informe nome e sobrenome'); return }
    if (!docDigits) { toast.error('Informe o CPF/CNPJ'); return }
    if (!telefone.trim()) { toast.error('Informe o telefone'); return }
    for (const [v, rot] of [[endereco, 'endereço'], [numero, 'número'], [bairro, 'bairro'],
                            [cidade, 'cidade'], [uf, 'UF']] as [string, string][]) {
      if (!v.trim()) { toast.error(`Informe o ${rot} (aba Completo)`); return }
    }
    // Trava: não salva documento inválido (vazio segue permitido no rápido).
    if (docInvalido) {
      toast.error(pessoaTipo === 'PF' ? 'CPF inválido — verifique os dígitos' : 'CNPJ inválido — verifique os dígitos')
      return
    }
    // Trava: não salva se o CPF/CNPJ já pertence a outro cadastro (backend também bloqueia com 409).
    if (docDup) {
      toast.error(`${docDup.tipo_doc} já cadastrado: ${docDup.nome}`)
      return
    }
    setSalvando(true)
    try {
      const base: any = {
        tipo: 'cliente',
        pessoa_tipo: pessoaTipo,
        nome: nome.trim(),
        documento: documento.trim() || null,
        telefone: telefone.trim() || null,
        email: email.trim() || null,
        indicador_ie: indicadorIe || '9',
        ativo: true,
      }
      if (modo === 'completo') {
        Object.assign(base, {
          nome_fantasia: nomeFantasia.trim() || null,
          ie: ie.trim() || null,
          cep: cep.trim() || null,
          endereco: endereco.trim() || null,
          numero: numero.trim() || null,
          bairro: bairro.trim() || null,
          cidade: cidade.trim() || null,
          uf: uf.trim() || null,
          complemento: complemento.trim() || null,
          codigo_ibge_cidade: codigoIbgeCidade.trim() || null,
          observacao: observacao.trim() || null,
        })
      }
      // O PUT sobrescreve TODO campo do PessoaIn (cadastros/routes.py:337). O modal só
      // conhece parte deles, então na edição mandamos o cadastro atual + o que mudou —
      // senão tabela de preço, rota, vendedores e dados bancários iriam a null.
      const { data } = pessoa?.id
        ? await api.put(`/cadastros/pessoas/${pessoa.id}`, { ...pessoa, ...base })
        : await api.post('/cadastros/pessoas', base)
      toast.success(pessoa?.id ? 'Cadastro atualizado' : 'Cliente cadastrado')
      onCriado(data)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao cadastrar cliente')
    } finally {
      setSalvando(false)
    }
  }

  const inputCls = `w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg
    focus:bg-white focus:border-brand-400 focus:outline-none`

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-ink-900 flex items-center gap-2">
            <UserPlus size={20} className="text-brand-600" /> Novo cliente
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-100 text-ink-500">
            <X size={18} />
          </button>
        </div>

        {/* Modo Rápido x Completo */}
        <div className="flex gap-2 mb-3 p-0.5 bg-ink-100 rounded-lg">
          <button type="button" onClick={() => setModo('rapido')}
            className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors
              ${modo === 'rapido' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600 hover:bg-ink-200'}`}>
            Rápido
          </button>
          <button type="button" onClick={() => setModo('completo')}
            className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors
              ${modo === 'completo' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600 hover:bg-ink-200'}`}>
            Completo
          </button>
        </div>

        <div className="space-y-3" onKeyDown={enterAdvance}>
          {/* Tipo PF/PJ */}
          <div className="flex gap-2">
            <button type="button" onClick={() => setPessoaTipo('PF')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
                ${pessoaTipo === 'PF' ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}>
              Pessoa Física
            </button>
            <button type="button" onClick={() => setPessoaTipo('PJ')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors
                ${pessoaTipo === 'PJ' ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}>
              Pessoa Jurídica
            </button>
          </div>

          <div>
            <label className="text-[11px] text-ink-500 uppercase tracking-wide">
              {pessoaTipo === 'PF' ? 'Nome' : 'Razão social'}*
            </label>
            <input ref={nomeRef} value={nome} onChange={e => setNome(e.target.value.toUpperCase())}
              placeholder={pessoaTipo === 'PF' ? 'Nome completo' : 'Razão social'}
              className={inputCls} />
          </div>

          {modo === 'completo' && pessoaTipo === 'PJ' && (
            <div>
              <label className="text-[11px] text-ink-500 uppercase tracking-wide">Nome fantasia</label>
              <input value={nomeFantasia} onChange={e => setNomeFantasia(e.target.value.toUpperCase())}
                placeholder="Nome fantasia" className={inputCls} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-ink-500 uppercase tracking-wide">
                {pessoaTipo === 'PF' ? 'CPF' : 'CNPJ'}
              </label>
              <input value={documento} onChange={e => setDocumento(e.target.value.replace(/\D/g, ''))}
                placeholder="Apenas dígitos" inputMode="numeric"
                className={`${inputCls} ${(docMostraErro || docDup) ? 'border-rose-500 focus:border-rose-500' : docValidoOk ? 'border-emerald-500 focus:border-emerald-500' : ''}`} />
              {checandoDoc ? (
                <p className="mt-1 text-[11px] text-ink-400 flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" /> verificando…
                </p>
              ) : docDup ? (
                <p className="mt-1 text-[11px] text-rose-600 font-medium">
                  {docDup.tipo_doc} já cadastrado: #{docDup.codigo || ''} {docDup.nome}
                </p>
              ) : docMostraErro ? (
                <p className="mt-1 text-[11px] text-rose-600 font-medium">
                  {pessoaTipo === 'PF' ? 'CPF inválido' : 'CNPJ inválido'}
                </p>
              ) : docValidoOk ? (
                <p className="mt-1 text-[11px] text-emerald-600 font-medium flex items-center gap-1">
                  <Check size={11} /> {pessoaTipo === 'PF' ? 'CPF' : 'CNPJ'} válido
                </p>
              ) : null}
            </div>
            <div>
              <label className="text-[11px] text-ink-500 uppercase tracking-wide">Telefone</label>
              {/* Máscara (xx) 9xxxx-xxxx aplicada pelo JS — usuário digita só os dígitos */}
              <TelefoneInput value={telefone} onChange={setTelefone} className={inputCls} />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-ink-500 uppercase tracking-wide">E-mail</label>
            <input value={email} onChange={e => setEmail(e.target.value.toLowerCase())}
              placeholder="email@exemplo.com" type="email" className={inputCls} />
          </div>

          {/* ===== Modo COMPLETO ===== */}
          {modo === 'completo' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">Indicador IE</label>
                  <select value={indicadorIe} onChange={e => setIndicadorIe(e.target.value)} className={inputCls}>
                    <option value="9">Não contribuinte</option>
                    <option value="1">Contribuinte ICMS</option>
                    <option value="2">Isento</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">Inscrição estadual</label>
                  <input value={ie} onChange={e => setIe(e.target.value)}
                    placeholder={indicadorIe === '1' ? 'IE' : 'ISENTO / —'} className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide flex items-center gap-1">
                    CEP {buscandoCep && <Loader2 size={10} className="animate-spin" />}
                  </label>
                  <input value={cep} onChange={e => setCep(e.target.value)} onBlur={onBlurCep}
                    placeholder="00000-000" inputMode="numeric" className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">Endereço</label>
                  <input value={endereco} onChange={e => setEndereco(e.target.value)}
                    placeholder="Logradouro" className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">Número</label>
                  <input value={numero} onChange={e => setNumero(e.target.value)}
                    placeholder="Nº" className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">Bairro</label>
                  <input value={bairro} onChange={e => setBairro(e.target.value)}
                    placeholder="Bairro" className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">Cidade</label>
                  <input value={cidade} onChange={e => setCidade(e.target.value)}
                    placeholder="Cidade" className={inputCls} />
                </div>
                <div>
                  <label className="text-[11px] text-ink-500 uppercase tracking-wide">UF</label>
                  <input value={uf} onChange={e => setUf(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="UF" maxLength={2} className={inputCls} />
                </div>
              </div>

              <div>
                <label className="text-[11px] text-ink-500 uppercase tracking-wide">Complemento</label>
                <input value={complemento} onChange={e => setComplemento(e.target.value)}
                  placeholder="Apto, bloco, referência..." className={inputCls} />
              </div>

              <div>
                <label className="text-[11px] text-ink-500 uppercase tracking-wide">Observação</label>
                <textarea value={observacao} onChange={e => setObservacao(e.target.value)}
                  rows={2} placeholder="Observações internas" className={inputCls} />
              </div>
            </>
          )}

          {modo === 'rapido' && (
            <div className="text-[11px] text-ink-500 bg-ink-50 rounded-lg px-3 py-2">
              Cadastro rápido — você pode completar endereço e dados fiscais depois em Cadastros &gt; Clientes,
              ou clicar em <b>Completo</b> acima.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={onClose} disabled={salvando}
              className="py-2.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 font-medium">
              Cancelar
            </button>
            <button onClick={salvar} disabled={salvando || !nome.trim() || docInvalido || !!docDup}
              title={docDup ? `${docDup.tipo_doc} já cadastrado` : docInvalido ? (pessoaTipo === 'PF' ? 'CPF inválido' : 'CNPJ inválido') : undefined}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700
                text-white font-semibold disabled:opacity-50">
              {salvando ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Salvar e selecionar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
